/**
 * CSV-Import für Portfolios. Erkennt Trennzeichen (; , Tab), Anführungszeichen
 * und gängige deutsche, französische und englische Spaltennamen.
 */
import { isLv95, lv95ToWgs84 } from '../geo/lv95';

export function detectDelimiter(text: string): string {
  const first = text.split(/\r?\n/).find((l) => l.trim()) ?? '';
  const counts = [';', ',', '\t'].map((d) => ({ d, n: first.split(d).length - 1 }));
  counts.sort((a, b) => b.n - a.n);
  return counts[0].n > 0 ? counts[0].d : ';';
}

export function parseCsv(text: string, delimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let q = false;
  const s = text.replace(/^﻿/, '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cell += '"';
          i++;
        } else q = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') q = true;
    else if (ch === delimiter) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

const norm = (h: string) =>
  h
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');

const ALIASES: Record<string, string[]> = {
  label: ['bezeichnung', 'name', 'label', 'objekt', 'objektname', 'gebaeude', 'liegenschaft', 'designation', 'nom'],
  address: ['adresse', 'address', 'anschrift', 'standort', 'adr'],
  street: ['strasse', 'street', 'rue', 'strassenr', 'strasseundnr'],
  number: ['nr', 'hausnummer', 'number', 'no', 'numero'],
  zip: ['plz', 'zip', 'postleitzahl', 'npa', 'postcode'],
  place: ['ort', 'ortschaft', 'gemeinde', 'city', 'localite', 'place'],
  egid: ['egid'],
  lat: ['lat', 'latitude', 'breite', 'breitengrad', 'wgs84lat'],
  lon: ['lon', 'lng', 'long', 'longitude', 'laenge', 'langengrad', 'laengengrad', 'wgs84lon'],
  e: ['e', 'east', 'easting', 'ost', 'xlv95', 'lv95e', 'koorde', 'gkode'],
  n: ['n', 'north', 'northing', 'nord', 'ylv95', 'lv95n', 'koordn', 'gkodn'],
};

export type Field = keyof typeof ALIASES;

export function mapHeader(header: string[]): Partial<Record<Field, number>> {
  const out: Partial<Record<Field, number>> = {};
  header.forEach((h, i) => {
    const n = norm(h);
    for (const [f, names] of Object.entries(ALIASES) as [Field, string[]][]) {
      if (out[f] === undefined && names.includes(n)) out[f] = i;
    }
  });
  return out;
}

export type RowRequest =
  | { kind: 'coords'; lat: number; lon: number }
  | { kind: 'egid'; egid: string }
  | { kind: 'address'; text: string }
  | { kind: 'invalid'; reason: string };

export interface ImportRow {
  line: number;
  label: string;
  req: RowRequest;
}

const numOf = (v: string | undefined) => {
  if (v === undefined) return NaN;
  const t = v.trim().replace(/['’ ]/g, '');
  if (t === '') return NaN;
  // Dezimalkomma nur, wenn kein Punkt vorhanden ist
  return Number(t.includes('.') ? t : t.replace(',', '.'));
};

export const MAX_IMPORT_ROWS = 1000;

export function interpretRows(rows: string[][]): { rows: ImportRow[]; header: Partial<Record<Field, number>>; error?: string } {
  if (rows.length < 2) return { rows: [], header: {}, error: 'Die Datei enthält keine Datenzeilen (erste Zeile = Spaltennamen).' };
  const header = mapHeader(rows[0]);
  const hasAddr = header.address !== undefined || header.street !== undefined;
  const hasCoords = (header.lat !== undefined && header.lon !== undefined) || (header.e !== undefined && header.n !== undefined);
  if (!hasAddr && !hasCoords && header.egid === undefined) {
    return { rows: [], header, error: 'Keine erkannte Spalte für Adresse, EGID oder Koordinaten. Erwartet z. B. «Bezeichnung; Adresse» oder «Bezeichnung; EGID» oder «Bezeichnung; Lat; Lon».' };
  }
  if (rows.length - 1 > MAX_IMPORT_ROWS) return { rows: [], header, error: `Zu viele Zeilen (${rows.length - 1}); höchstens ${MAX_IMPORT_ROWS} pro Import.` };
  const out: ImportRow[] = rows.slice(1).map((r, k) => {
    const get = (f: Field) => (header[f] !== undefined ? (r[header[f]!] ?? '').trim() : '');
    const street = [get('street'), get('number')].filter(Boolean).join(' ');
    const address = get('address') || [street, [get('zip'), get('place')].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const label = get('label') || address || `Zeile ${k + 2}`;
    const line = k + 2;
    const lat = numOf(get('lat'));
    const lon = numOf(get('lon'));
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return { line, label, req: { kind: 'invalid', reason: 'Koordinaten ausserhalb des gültigen Bereichs' } };
      return { line, label, req: { kind: 'coords', lat, lon } };
    }
    const e = numOf(get('e'));
    const n = numOf(get('n'));
    if (Number.isFinite(e) && Number.isFinite(n)) {
      if (!isLv95(e, n)) return { line, label, req: { kind: 'invalid', reason: 'LV95-Koordinaten ausserhalb der Schweiz' } };
      const w = lv95ToWgs84(e, n);
      return { line, label, req: { kind: 'coords', lat: w.lat, lon: w.lon } };
    }
    const egid = get('egid').replace(/\D/g, '');
    if (egid) {
      if (egid.length > 9) return { line, label, req: { kind: 'invalid', reason: 'EGID mit mehr als 9 Ziffern' } };
      return { line, label, req: { kind: 'egid', egid } };
    }
    if (address.length >= 3) return { line, label, req: { kind: 'address', text: address } };
    return { line, label, req: { kind: 'invalid', reason: 'Weder Koordinaten, EGID noch Adresse angegeben' } };
  });
  return { rows: out, header };
}

/** Prüft, ob ein Adresstreffer zur gesuchten Adresse passt (Strasse und Hausnummer). */
export function addressMatches(query: string, label: string): boolean {
  const tokens = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/str\./g, 'strasse')
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  const q = tokens(query);
  const l = new Set(tokens(label));
  const num = q.find((t) => /^\d+[a-z]?$/.test(t) && t.length <= 4);
  const words = q.filter((t) => !/^\d+$/.test(t) && t.length > 2);
  return (!num || l.has(num)) && words.slice(0, 1).every((w) => l.has(w));
}
